import { Component, Vue } from 'vue-property-decorator';

@Component
export default class LazyLoadMixin extends Vue {
  private intersectionObserver: IntersectionObserver | null = null;
  private isVisible: boolean = false;
  private hasBeenVisible: boolean = false;

  public mounted(): void {
    if (this.shouldUseLazyLoading()) {
      this.setupVisibilityObserver();
    } else {
      // If not using lazy loading, mark as visible immediately
      this.isVisible = true;
      this.hasBeenVisible = true;
      this.onBecomeVisible();
    }
  }

  public beforeDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  private shouldUseLazyLoading(): boolean {
    // Override this method in components that use this mixin
    return (this as any).lazyLoad === true;
  }

  private setupVisibilityObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback for browsers without IntersectionObserver
      this.isVisible = true;
      this.hasBeenVisible = true;
      this.onBecomeVisible();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasVisible = this.isVisible;
          this.isVisible = entry.isIntersecting;

          // Only trigger onBecomeVisible when transitioning from invisible to visible
          if (!wasVisible && this.isVisible && !this.hasBeenVisible) {
            this.hasBeenVisible = true;
            this.onBecomeVisible();
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: '50px', // Start loading 50px before coming into view
        threshold: 0.1, // Trigger when 10% of element is visible
      }
    );

    // Start observing this component's root element
    this.$nextTick(() => {
      if (this.$el && this.intersectionObserver) {
        this.intersectionObserver.observe(this.$el);
      }
    });
  }

  // Override this method in components that use this mixin
  protected onBecomeVisible(): void {
    // This method should be implemented by components using the mixin
    console.log('Component became visible - override onBecomeVisible method');
  }

  // Getter for components to check visibility state
  get isCurrentlyVisible(): boolean {
    return this.isVisible;
  }

  // Getter for components to check if they've ever been visible
  get hasEverBeenVisible(): boolean {
    return this.hasBeenVisible;
  }
}